/**
 * Keyboard shortcuts and event handlers for the recipe editor
 */

import {Prec} from "@codemirror/state";
import {EditorView, keymap} from "@codemirror/view";
import {acceptCompletion, startCompletion} from "@codemirror/autocomplete";
import {tokensField, setTokensEffect} from "../state/editor.state";
import {
  cancelAnyDraft,
  commitDraft,
  tryStartDraftAtCursor,
  startIngredientDraftAtCursor,
  getDraft
} from "../utils/draft.utils";

/**
 * Check if view has non-empty selection
 */
function hasNonEmptySelection(view: EditorView): boolean {
  return view.state.selection.ranges.some((r) => r.from !== r.to);
}

/**
 * Create keymap for accepting completion and creating chips
 */
export function createAcceptCompletionKeymap(
  singleLine: boolean,
  log: (msg: string, data?: any) => void
) {
  return Prec.highest(
    keymap.of([
      {
        key: "Enter",
        run: (view) => {
          const accepted = acceptCompletion(view);

          // If autocomplete was accepted, the apply function handles everything
          // Don't do any additional processing
          if (accepted) {
            return true;
          }

          return singleLine;
        },
      },
      {
        key: "Backspace",
        run: (view) => {
          if (!hasNonEmptySelection(view)) return false;

          const tokens = view.state.field(tokensField);
          if (tokens.length === 0) return false;

          log("🧹 selection delete -> reset tokens", {tokenCount: tokens.length});
          view.dispatch({effects: setTokensEffect.of([])});

          return false;
        },
      },
    ])
  );
}

/**
 * Create keymap for canceling draft (Ctrl/Cmd+C)
 */
export function createCancelDraftKeymap(log: (msg: string, data?: any) => void) {
  return Prec.highest(
    keymap.of([
      {
        key: "Ctrl-c",
        run: (view) => {
          if (hasNonEmptySelection(view)) return false;
          return cancelAnyDraft(view, log);
        },
      },
      {
        key: "Mod-c",
        run: (view) => {
          if (hasNonEmptySelection(view)) return false;
          return cancelAnyDraft(view, log);
        },
      },
    ])
  );
}

/**
 * Create commit handlers (Tab, comma, period, @)
 */
export function createCommitHandlers(log: (msg: string, data?: any) => void) {
  return EditorView.domEventHandlers({
    keydown: (event, view) => {
      const key = event.key;

      // Start draft on "@"
      if (key === "@") {
        event.preventDefault();
        startIngredientDraftAtCursor(view, log);
        startCompletion(view);
        return true;
      }

      // Commit draft on Tab, comma, period
      const isCommitKey = key === "Tab" || key === "," || key === ".";
      if (!isCommitKey) return false;

      const res = commitDraft(view, log);
      if (!res.ok || res.endPos == null) return false;

      event.preventDefault();

      const end = res.endPos;
      const doc = view.state.doc;

      const setCursor = (pos: number) => ({
        selection: {anchor: pos, head: pos},
        scrollIntoView: true,
      });

      if (key === "Tab") {
        const nextChar = end < doc.length ? doc.sliceString(end, end + 1) : "";
        if (nextChar === " ") {
          view.dispatch(setCursor(end + 1));
        } else {
          view.dispatch({changes: {from: end, to: end, insert: " "}, ...setCursor(end + 1)});
        }
        return true;
      }

      view.dispatch({changes: {from: end, to: end, insert: key}, ...setCursor(end + 1)});
      return true;
    },
  });
}

/**
 * Create draft engine update listener
 */
export function createDraftEngine(log: (msg: string, data?: any) => void) {
  const MIN_CHARS = 2;

  return EditorView.updateListener.of((u) => {
    if (!u.docChanged) return;

    tryStartDraftAtCursor(u.view, log);

    const isTyping = u.transactions.some((tr) => tr.isUserEvent("input"));
    if (!isTyping) return;

    const tokens = u.state.field(tokensField);
    const draft = getDraft(tokens);
    if (!draft) return;

    const pos = u.state.selection.main.head;
    const typed = u.state.doc.sliceString(draft.from, pos).trim();

    if (typed.length >= MIN_CHARS) {
      startCompletion(u.view);
    }
  });
}
